'use client';

import React, { useEffect, useRef, useCallback, useId } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: ModalSize;
}

const sizeClasses: Record<ModalSize, string> = {
    sm: 'md:max-w-sm',
    md: 'md:max-w-md',
    lg: 'md:max-w-lg',
    xl: 'md:max-w-xl',
    '2xl': 'md:max-w-2xl',
    '4xl': 'md:max-w-4xl',
};

export function Modal({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'md',
}: ModalProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const reactId = useId();
    const titleId = `modal-title-${reactId}`;
    const descId = `modal-desc-${reactId}`;

    // Body scroll lock
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }
    }, [open]);

    // Focus trap + Escape
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key === 'Tab' && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                );
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        },
        [onClose],
    );

    // Auto-focus first focusable element on open
    useEffect(() => {
        if (open && dialogRef.current) {
            const timer = setTimeout(() => {
                const focusable = dialogRef.current?.querySelector<HTMLElement>(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                );
                focusable?.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Backdrop click
    const handleBackdropClick = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) onClose();
        },
        [onClose],
    );

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={handleBackdropClick}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
        >
            <div
                ref={dialogRef}
                className={`
                    w-full h-full md:w-auto md:h-auto ${sizeClasses[size]}
                    bg-bg-surface border border-border-default
                    rounded-none md:rounded-2xl
                    shadow-2xl shadow-zinc-950/40
                    flex flex-col
                    md:mx-4
                `}
            >
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-2">
                    <div className="flex-1 min-w-0">
                        <h2
                            id={titleId}
                            className="text-base font-semibold text-zinc-100 text-balance"
                        >
                            {title}
                        </h2>
                        {description && (
                            <p
                                id={descId}
                                className="mt-1 text-sm text-zinc-400 leading-relaxed text-pretty"
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    <Button
                        type="button"
                        onClick={onClose}
                        className="
                            ml-4 p-1.5 rounded-lg
                            text-zinc-500 hover:text-zinc-300 hover:bg-white/5
                            transition-colors duration-150
                            focus-visible:ring-2 focus-visible:ring-zinc-500/50 focus-visible:outline-none
                        "
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-end gap-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
